import {
    calendarNotificationsBarDeleteCalendarFailed,
    calendarNotificationsBarDeleteCalendar,
} from './deleteCalendar.locstring.json';
import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';
import loc, { format } from 'owa-localize';
import { getCalendars } from 'owa-calendar-surface-calendars-store';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import {
    removeCalendarFromCalendarsCache,
    Calendar,
    getChangeKeyByCalendarItemId,
} from 'owa-calendar-cache';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { trace } from 'owa-trace';
import { ControlIcons } from 'owa-control-icons';
import showUndoNotificationForDelete from '../utils/showUndoNotificationForDelete';
import { onBeforeCalendarDelete } from './onBeforeCalendarDelete';
import showCalendarErrorNotification from './showCalendarErrorNotification';
import * as Schema from 'owa-graph-schema';
import { invokeDeleteCalendarMutation } from '../services/deleteCalendarService';

/**
 * Action to delete calendar
 * @param calendar calendar to delete
 */
export let deleteCalendar = createLegacyActionWithDatapoint('DeleteCalendar')(
    async function deleteCalendarInternal(calendar: Calendar): Promise<boolean> {
        const dataPoint = returnTopExecutingActionDatapoint();
        let deletedCalendarIndex = -1;
        onBeforeCalendarDelete(calendar);
        let wasSuccessful = true;
        deletedCalendarIndex = removeCalendarLocally(getCalendars(), calendar);

        if (dataPoint) {
            dataPoint.markUserPerceivedTime(); // mark user perceived time here as soon as we dispatch the request to delete
        }

        // this notification gives the user a chance to cancel their action before actually sending to the server
        // if shouldUndo returns true, we return back without calling handleCalendarServiceError
        let shouldUndo = await showUndoNotificationForDelete(
            'deleteCalendar',
            format(loc(calendarNotificationsBarDeleteCalendar), calendar.displayName)
        );
        if (shouldUndo) {
            restoreDeletedCalendar(getCalendars(), deletedCalendarIndex, calendar);
            return true;
        }

        const deleteCalendarResult = await handleDeleteCalendarRequest(calendar);
        if (deleteCalendarResult == null) {
            // creating inline function to give access to local wasSuccessful variable
            restoreDeletedCalendar(getCalendars(), deletedCalendarIndex, calendar);
            wasSuccessful = false;

            showCalendarErrorNotification(
                'deleteCalendar',
                format(loc(calendarNotificationsBarDeleteCalendarFailed), calendar.displayName),
                ControlIcons.Error,
                loc(tryAgainLabel),
                () => deleteCalendar(calendar)
            );
        }

        return wasSuccessful;
    }
);

async function handleDeleteCalendarRequest(
    calendar: Calendar
): Promise<Schema.DeleteCalendarResult> {
    try {
        const deleteCalendarMutationResult = await invokeDeleteCalendarMutation(
            calendar.id,
            getChangeKeyByCalendarItemId(calendar.id.id)
        );

        if (deleteCalendarMutationResult) {
            handleSuccessfulServiceResponse(calendar);
        }

        return deleteCalendarMutationResult;
    } catch {
        return null;
    }
}

function handleSuccessfulServiceResponse(calendar: Calendar) {
    trace.info(`[deleteCalendar] Removing calendar: ${calendar.id}`);
    removeCalendarFromCalendarsCache(calendar);
}

/**
 * Removes the given calendar from the calendars list and returns the index it was removed at, or -1 if not found
 */
function removeCalendarLocally(calendars: Calendar[], calendarToRemove: Calendar): number {
    // Remove calendar from list of calendars instantly. It will be restored if action fails
    const foundCalendarResult = findInArray(calendars, item => {
        return item.id == calendarToRemove.id;
    });
    if (foundCalendarResult) {
        calendars.splice(foundCalendarResult.index, 1);
        return foundCalendarResult.index;
    }

    return -1;
}

function restoreDeletedCalendar(
    calendars: Calendar[],
    calendarsIndex: number,
    calendarToInsert: Calendar
) {
    if (calendarsIndex >= 0) {
        calendars.splice(calendarsIndex, 0, calendarToInsert);
    }
}

export default deleteCalendar;
